import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";

const PASSWORD_REQUIREMENTS = [
  { test: (p) => p.length >= 8, label: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "One number" },
  { test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p), label: "One special character" },
];

export default function Login() {
  const navigate = useNavigate();
  const { setIsAuthenticated, setRole, setAdminEmail, setCarrierEmail, setShipperEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError("Please enter email and password"); return; }
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.error || "Invalid credentials");
      }
      const data = await response.json();
      const user = data.user;
      if (!user || !user.role) throw new Error("Invalid response from server");

      // Store auth
      localStorage.setItem("roadoptix_user", JSON.stringify(user));
      localStorage.setItem("roadoptix_token", data.token);
      setIsAuthenticated(true);

      // Route based on role
      const role = user.role || "";
      if (role.startsWith("BROKER_") || role === "ADMIN") {
        setRole("BROKER");
        setAdminEmail(user.email);
        navigate("/app/broker");
      } else if (role === "CARRIER_USER" || role.startsWith("CARRIER")) {
        setRole("CARRIER");
        setCarrierEmail(user.email);
        navigate("/app/carrier/assigned");
      } else if (role === "SHIPPER_USER" || role.startsWith("SHIPPER")) {
        setRole("SHIPPER");
        setShipperEmail(user.email);
        navigate("/app/shipper/loads");
      } else {
        setRole("BROKER");
        setAdminEmail(user.email);
        navigate("/app/broker");
      }
    } catch (e) {
      setError(e.message || "Login failed");
    } finally { setLoading(false); }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    if (!forgotEmail) { setError("Please enter your email"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } catch (e) {
      setForgotSent(true); // Still show success
    } finally { setLoading(false); }
  }

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 8,
    border: "1px solid #d1d5db", fontSize: 15, outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    }}>
      <div style={{
        background: "white", borderRadius: 16, padding: 40, width: 420,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#DC2626", letterSpacing: -1 }}>RoadOptix</div>
          <div style={{ color: "#6b7280", fontSize: 14, marginTop: 4 }}>
            {mode === "login" ? "Sign in to your account" : "Reset your password"}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: "10px 14px", marginBottom: 16, borderRadius: 8,
            background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* LOGIN FORM */}
        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email Address</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" autoComplete="email" autoFocus
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password" autoComplete="current-password"
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", fontSize: 18,
                    color: "#6b7280", padding: 4,
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            {/* Password strength indicator */}
            {password.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                  {[1,2,3,4,5].map(i => {
                    const passed = PASSWORD_REQUIREMENTS.filter(r => r.test(password)).length;
                    return (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i <= passed
                          ? passed <= 2 ? "#dc2626" : passed <= 3 ? "#f59e0b" : "#059669"
                          : "#e5e7eb"
                      }} />
                    );
                  })}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px" }}>
                  {PASSWORD_REQUIREMENTS.map((req, i) => (
                    <div key={i} style={{ fontSize: 11, color: req.test(password) ? "#059669" : "#9ca3af" }}>
                      {req.test(password) ? "✓" : "○"} {req.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ textAlign: "right", marginBottom: 20 }}>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
                style={{ background: "none", border: "none", color: "#2563eb", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
                Forgot password?
              </button>
            </div>

            <button type="submit" disabled={loading}
              style={{
                width: "100%", padding: "12px", borderRadius: 8, border: "none",
                background: loading ? "#9ca3af" : "linear-gradient(135deg, #DC2626, #b91c1c)",
                color: "white", fontSize: 16, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading ? "none" : "0 4px 12px rgba(220,38,38,0.4)",
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        )}

        {/* FORGOT PASSWORD FORM */}
        {mode === "forgot" && !forgotSent && (
          <form onSubmit={handleForgotPassword}>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email Address</label>
              <input
                type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                placeholder="you@company.com" autoFocus
                style={inputStyle}
              />
            </div>
            <button type="submit" disabled={loading}
              style={{
                width: "100%", padding: "12px", borderRadius: 8, border: "none",
                background: loading ? "#9ca3af" : "#2563eb", color: "white",
                fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" onClick={() => { setMode("login"); setError(""); setForgotSent(false); }}
                style={{ background: "none", border: "none", color: "#2563eb", fontSize: 13, cursor: "pointer" }}>
                ← Back to Sign In
              </button>
            </div>
          </form>
        )}

        {/* FORGOT PASSWORD SUCCESS */}
        {mode === "forgot" && forgotSent && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
            <h3 style={{ margin: "0 0 8px", fontWeight: 700 }}>Check Your Email</h3>
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20 }}>
              If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link.
            </p>
            <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }}
              style={{
                padding: "10px 20px", borderRadius: 8, border: "none",
                background: "#2563eb", color: "white", fontWeight: 700, cursor: "pointer",
              }}
            >
              Back to Sign In
            </button>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 24, textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
          Powered by <strong>RoadOptix</strong> © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  

    );
}
